<template>
  <div class="webrtc-view">
    <div class="webrtc-view__tracks">
      <div class="webrtc-view__not-connected" v-if="!isPatientCameraPlaying">
        <p class="webrtc-view__connecting-text" v-if="connecting">Connecting ...</p>
        <button v-if="cancelable || !connecting" @click="cancelable ? stopVideo() : startVideo()"
          :class="{ 'is-danger': cancelable }" class="button is-primary webrtc-view__connect-btn">{{ connectButtonText
          }}</button>
      </div>
      <div class="webrtc-view__video-container">
        <div class="open-mic-warning" v-show="!muted && isPatientCameraPlaying">
          MIC IS OPEN
        </div>
        <video v-if="isTrainingVideo && isPatientCameraPlaying" ref="video" loop class="training-video"
          autoplay="autoplay" muted="muted" tabindex="-1" preload="auto" :src="meetingMetaData?.device.video_url"
          data-private></video>
        <audio v-if="isTrainingVideo && isAimeeEnabled" autoplay="autoplay" ref="aimeeAudio"></audio>
        <template v-else>
          <teleport to=".quality-indicator-teleport" defer>
            <quality-indicator v-if="isPatientCameraPlaying && isQualityIndicatorEnabled && !isTrainingVideo"
              ref="qualityIndicator" is-video-meet :wifi="wifiMetrics"
              :is-wifi-network-interface="isWifiNetworkInterface" />
          </teleport>
          <video id="webrtc-view__patient-view" autoplay playsinline v-show="isPatientCameraPlaying" ref="webrtcView"
            data-private></video>
          <div class="overlay-container">
            <div id="webrtc-view__other-participants" v-show="otherParticipants.length > 0 || isPatientCameraPlaying">
              <!-- local participant -->
              <div v-if="!meetingMetaData.is_preview && isPatientCameraPlaying && videoSettings?.activateVideo"
                class="participant-container">
                <video id="webrtc-view__local-view" autoplay playsinline muted="true" data-private></video>
                <div class="participant-name">You</div>
              </div>
              <!-- other participants -->
              <div v-for="p in otherParticipants" :key="p.participantId" class="participant-container">
                <!-- NB: other elements on the page may use this participant id so make sure this is unique -->
                <video autoplay playsinline :id="`other-video-${p.participantId}`" data-private></video>
                <div class="participant-name">{{ p.displayName || p.participantId }}</div>
              </div>
            </div>
            <div class="bottom-row">
              <ptz-presets v-show="isPatientCameraPlaying && isExpanded && meetingMetaData.is_ptz"
                :element="$refs.webrtcView" :webrtcSession="webrtcSession"
                :video-device-id="meetingMetaData?.device?.id"></ptz-presets>
              <digital-ptz v-show="isPatientCameraPlaying && isExpanded && !meetingMetaData.is_ptz"
                :popup-mode="isExpanded" class="video-ptz" :element="$refs.webrtcView" :webrtcSession="webrtcSession"
                :supports-night-mode="supportsNightMode"></digital-ptz>
              <ptz v-show="isPatientCameraPlaying && isExpanded && meetingMetaData.is_ptz"
                :supports-night-mode="supportsNightMode" :webrtcSession="webrtcSession" :popup-mode="isExpanded"
                class="video-ptz" :element="$refs.webrtcView">
              </ptz>
            </div>
            <!-- disable transcription of patient 
            <live-transcription :active="transcriptionEnabled && isPatientCameraPlaying"
              :audio-input-stream="getPatientAudioStream()" :patient-monitor-id="cachedPatientMonitorId"
              ref="liveTranscription">
            </live-transcription>
            -->
          </div>
        </template>
        <div class="webrtc-view__video-controls" :class="{ 'webrtc-view__video-controls--expanded': isExpanded }"
          v-show="isPatientCameraPlaying">
          <button @click="stopVideo" class="button hangup-button webrtc-view__video-control">
            <i class="mdi mdi-phone-hangup" aria-hidden="true"></i>
          </button>
          <button @click="toggleMute" class="button webrtc-view__video-control">
            <i :class="['mdi', muted ? 'mdi-microphone-off' : 'mdi-microphone']" aria-hidden="true"></i>
          </button>
          <button v-if="!isTrainingVideo && isClosedCaptionsEnabled" @click="toggleTranscription"
            class="button webrtc-view__video-control">
            <img :src="transcriptionEnabled ? ccOnIcon : ccSlashIcon" alt="Closed Captions" class="cc-icon">
          </button>
          <button v-if="isTrainingVideo && isAimeeEnabled"
            @click="aimeeConnected ? disconnectAimeeDemo() : connectAimeeDemo()"
            :class="['button webrtc-view__video-control', { 'is-success': aimeeConnected }]">
            <img src="@/assets/aimee.png" alt="Aimee" class="aimee-icon">
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import to from "@/lib/to";
import { mapState, mapMutations } from "vuex";
import { markRaw } from "vue";
import DigitalPtz from "@/components/DigitalPtz";
import Ptz from "@/components/Ptz";
import PtzPresets from "@/components/PtzPresets";
import { WebRTCSession, SessionTypes, SessionEvents } from "@/lib/webrtc";
import { LOG_TYPES } from "@/lib/Log";
import QualityIndicator from "@/components/QualityIndicator.vue";
import { TranscriptionSession } from "@/lib/webrtc";
import * as semver from 'semver';
import ccOnIcon from "@/assets/icons/cc-on.png";
import ccSlashIcon from "@/assets/icons/cc-slash.png";

export default {
  name: "WebrtcView",
  components: { DigitalPtz, Ptz, PtzPresets, QualityIndicator },
  props: {
    startAutomatically: {
      type: Boolean,
      required: false,
      default: false
    },
    isExpanded: {
      type: Boolean,
      required: false,
      default: false
    },
    videoSettings: {
      type: Object,
      required: false,
      default: () => {
        return {
          doorKnock: true,
          visitorName: '',
          activateVideo: false,
          localMediaStream: null,
          activateScreenShare: false,
        }
      }
    },
    meetingMetaData: {
      type: Object,
      required: false,
      default: () => { return {} }
    }
  },
  methods: {
    connectParticipantMedia(participant) {
      // NOTE: this may be called multiple times for the same participant
      const isPatient = participant.isPatient;
      console.log(`participant ${participant.participantId} connected`, participant);
      if (!isPatient) {
        const pIndex = this.otherParticipants.findIndex((p) => p.participantId === participant.participantId);
        if (pIndex === -1) {
          // need to add this to array first so that vue will allocate the div element
          this.otherParticipants.push(participant);
          console.debug(`added participant ${participant.participantId}. ${this.otherParticipants.length} total`);
        } else {
          console.debug(`already added participant ${participant.participantId}`);
          // still need to continue processing in case the stream changed
        }
      }

      const container = isPatient ? document.getElementById('webrtc-view__patient-view') : document.getElementById('webrtc-view__other-participants');
      if (isPatient) {
        if (container.srcObject !== participant.mediaStream) {
          console.log(`updated patient stream ${participant.mediaStream.id}`);
          container.srcObject = participant.mediaStream;
        }
        this.connecting = false;
        this.setPatientCameraIsPlaying(true);
        // Set local stream after camera is playing and element exists
        this.$nextTick(() => {
          this.setLocalMediaStream();
        });
        // Preload patient monitor ID for future use (Aimee, transcription, etc.)
        this.getPatientMonitorId();
        if (this.meetingMetaData.test_mode) {
          this.testingTimer = setTimeout(() => {
            this.stopVideo()
          }, this.testVideoDuration)
        }
      } else {
        this.$nextTick(() => {
          // make sure the video element is in the DOM before setting the stream
          const pDiv = document.getElementById(`other-video-${participant.participantId}`);
          if (!pDiv) {
            console.error(`unable to find participant div other-video-${participant.participantId}`);
            return;
          } else if (pDiv.tagName !== 'VIDEO') {
            console.error(`participant element other-video-${participant.participantId} is not a video element. Got: ${pDiv.tagName}`);
            return;
          }

          if (pDiv && pDiv.srcObject !== participant.mediaStream) {
            pDiv.srcObject = participant.mediaStream;
            console.log(`added other participant (${participant.participantId}) stream ${participant.mediaStream.id}`, pDiv);
          }
        });
      }
    },
    disconnectParticipant(participant) {
      // NOTE: this may be called multiple times for the same participant since it's called for each track
      console.log(`participant ${participant.participantId} tracks disconnected`, participant);
      if (!participant.isPatient) {
        // look for the remote participant
        const pIndex = this.otherParticipants.findIndex((p) => p.participantId === participant.participantId);
        if (pIndex !== -1) {
          // remove from the array - vue will remove the control automatically. the tracks will be handled by the unsubscribe handler
          this.otherParticipants.splice(pIndex, 1);
          const pVid = document.getElementById(`other-video-${participant.participantId}`);
          if (pVid) {
            pVid.srcObject = null;
            console.debug("participant video element cleared");
          }
        } else {
          console.debug('unable to find participant - assuming already removed');
        }
      } else {
        // lost the patient connection. disconnect from the room without closing it on the server
        // we leave open on the server so that there's an indication that there's a problem;
        // otherwise it would appear as if the user closed the room
        console.warn("Lost patient connection");
        this.stopVideo();
        this.$buefy.dialog.alert({
          title: 'Error',
          message: 'Video connection lost. Please try to reconnect.'
        });
      }
    },
    async startVideo() {
      this.log(LOG_TYPES.LIVE_VIDEO.CONNECT_STARTED)

      let videoReasonIds = []
      if (this.videoSettings.selectedVideoReasons && this.videoSettings.selectedVideoReasons.length > 0) {
        videoReasonIds = this.videoSettings.selectedVideoReasons.map(r => r.id)
      } else if (this.videoSettings.selectedVideoReason) {
        videoReasonIds = [this.videoSettings.selectedVideoReason]
      }

      let postData = {
        device_id: this.meetingMetaData.device.id,
        patient_id: this.meetingMetaData.patient?.id,
        play_greeting: this.videoSettings.doorKnock,
        ptz: this.meetingMetaData.is_ptz,
        two_way_video: this.videoSettings.activateVideo,
        video_reason_ids: videoReasonIds,
        virtual_request_id: this.meetingMetaData.virtual_request_id
      }
      if (this.isTrainingVideo) {
        const [err, response] = await to(this.http.post('/videos/join_meeting', postData, {
          handleErrors: true
        }))

        if (!err) {
          const { data: joinData } = response
          this.meetingId = joinData.meeting_id
          this.$emit('meeting-id', this.meetingId)
        }

        this.log(LOG_TYPES.LIVE_VIDEO.CONNECT_SUCCEEDED)
        this.setPatientCameraIsPlaying(true)
        this.connecting = false
        const webrtcSession = new WebRTCSession();
        this.webrtcSession = markRaw(webrtcSession);
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
        this.localMediaStream = markRaw(stream)

        // Set local stream after camera is playing and element exists
        this.$nextTick(() => {
          this.setLocalMediaStream();
        });

        if (this.meetingMetaData.auto_aimee && this.isAimeeEnabled) {
          this.connectAimeeDemo()
        }
      } else {
        this.connecting = true
        this.connectionTimeout = setTimeout(() => {
          if (!this.isPatientCameraPlaying) {
            this.stopVideo()
            this.$buefy.dialog.alert("Can't connect to patient's camera")
            this.log(LOG_TYPES.LIVE_VIDEO.VIDEO_CONNECT_FAILED)
          }
        }, 60000)

        let joinUrl = '/videos/join_meeting'

        if (this.meetingMetaData.test_mode) {
          postData = { device_id: this.meetingMetaData.device.id, ptz: this.meetingMetaData.is_ptz }
          joinUrl = '/videos/join_testing'
        }

        const [err, response] = await to(this.http.post(joinUrl, postData, {
          handleErrors: true
        }))

        if (!err) {
          const { data: joinData } = response
          this.meetingId = joinData.meeting_id
          this.$emit('meeting-id', this.meetingId)

          const webrtcSession = new WebRTCSession();
          // const webrtcSession = new VideoRoomManager()
          this.webrtcSession = markRaw(webrtcSession);

          webrtcSession.on(SessionEvents.signalRequired, this.sendSignal);

          webrtcSession.on(SessionEvents.participantTrackAdded, this.connectParticipantMedia);

          // listen for disconnections in order to cleanup
          webrtcSession.on(SessionEvents.participantDisconnected, this.disconnectParticipant);

          // listen for disconnected from room event
          webrtcSession.on(SessionEvents.disconnected, (error) => {
            // error is null/undefined when disconnected by us so ignore that case
            if (error != null) {
              console.warn(`room disconnected: ${error}`);

              let err_message = "You were disconnected from the video room. Please try again.";

              // clean up
              this.stopVideo(); // no wait needed

              this.$buefy.dialog.alert({
                title: 'Error',
                message: err_message,
              });


              this.log(LOG_TYPES.LIVE_VIDEO.VIDEO_DISCONNECT_UNEXPECTED)
            } else {
              console.debug('ignoring disconnected event');
              this.log(LOG_TYPES.LIVE_VIDEO.VIDEO_DISCONNECT_EXPECTED)
            }
          });

          webrtcSession.on(SessionEvents.webrtcNetworkScoreUpdated, (metrics) => {
            this.saveVideoMetrics('stats', metrics);
          });
          webrtcSession.on(SessionEvents.webrtcIssue, (issue) => {
            this.saveVideoMetrics('warning', issue);
          });
          webrtcSession.on(SessionEvents.connectSucceeded, this.onConnectSucceeded)

          webrtcSession.on(SessionEvents.webrtcQualityUpdated, ({ scores, issues }) => {
            if (!this.isQualityIndicatorEnabled || !this.$refs.qualityIndicator) {
              return;
            }
            if (scores?.inbound) {
              this.$refs.qualityIndicator.score = scores.inbound
              this.$refs.qualityIndicator.stats = scores.statsSamples.inboundStatsSample
            }
            if (issues) {
              console.warn(`webrtc quality issue detected for meeting ${this.meetingId}`, issues);
              this.$refs.qualityIndicator.issues = issues
            }
          });

          const statsInterval = 10000 // Always collect stats every 10s for video meetings.
          const roomType = this.meetingMetaData.test_mode ? SessionTypes.test : SessionTypes.meeting
          let stream;
          let isPresenter = false;
          if (this.videoSettings.activateVideo && this.videoSettings.activateScreenShare) {
            console.debug("activating screen share");
            // request screen for video
            stream = await navigator.mediaDevices.getDisplayMedia({
              video: true,
              audio: false,  // audio here would only be from a tab or application window
            })
            // add microphone audio track
            const micStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
            micStream.getAudioTracks().forEach((track) => {
              stream.addTrack(track);
            });
            isPresenter = true
          } else {
            const constraints = {
              audio: true,
              // limit video being sent b/c it is either displayed small or in kiosk mode where it's limited by CPU power on device
              video: this.videoSettings.activateVideo ? { width: 480, aspectRatio: 1.7777777778, frameRate: { min: 15 } } : false,
            }
            stream = await navigator.mediaDevices.getUserMedia(constraints)
            if (!this.videoSettings.activateVideo) {  // mute if no video to come in silently
              stream.getAudioTracks().forEach((track) => {
                track.enabled = false
              });
              this.muted = true
            }
          }
          this.localMediaStream = markRaw(stream)

          // Local stream will be set when patient camera starts playing
          await webrtcSession.connect(joinData, roomType, this.localMediaStream, {
            statsIntervalMs: statsInterval,
            userDisplayName: this.videoSettings.visitorName,
            webrtcQualityEnabled: this.isQualityIndicatorEnabled,
            isPresenter,
          })

        } else {
          console.error("Unable to open video room", err);
          let msg = 'Video session could not connect or was disconnected. Please check that monitoring has not been stopped and your network connection is functioning'
          if (err.response?.data?.error == 'meeting-another-camera-in-use') {
            const ptzFailed = !this.meetingMetaData.is_ptz
            msg = `A meeting is in progress with the ${ptzFailed ? 'Pan-Tilt-Zoom' : 'core'} camera. Please reconnect with Pan-Tilt-Zoom Camera option ${ptzFailed ? 'checked' : 'unchecked'} to join the meeting.`
          }
          this.$buefy.dialog.alert({
            title: 'Error',
            message: msg
          });
          this.log(LOG_TYPES.LIVE_VIDEO.VIDEO_CONNECT_FAILED)
          this.connecting = false
          clearTimeout(this.connectionTimeout)
          this.$emit('close')
        }
      }
    },
    async saveVideoMetrics(metricType, payload) {
      const deviceId = this.meetingMetaData.device?.id,
        divisionId = this.meetingMetaData.patient_monitor?.division_id || this.meetingMetaData.patient?.division_id
      if (!deviceId || !divisionId) {
        return;
      }

      const postData = payload.map(pl => {
        return {
          timestamp: pl.timestamp,
          metric_type: metricType,
          video_device_id: deviceId,
          division_id: divisionId,
          meeting_id: this.meetingId,
          metrics: pl.metrics
        }
      })
      to(this.http.post('/videos/save_metrics', { video_device_metrics: postData }, {
        handleErrors: false,
        skipLoading: true,
        skipDivisions: true,
        skipInactivityCheck: true
      }))
    },
    async transcribeToPatientTV() {
      if (!this.webrtcSession) {
        console.warn('No active webrtc session for transcription to patient TV');
        return;
      }

      const patientMonitorId = await this.getPatientMonitorId();

      console.info("Starting caregiver transcription to patient TV...");

      const thisView = this;
      function onTranscriptionLine(transcriptionLine) {
        console.log('Caregiver transcription to patient TV line:', transcriptionLine);
        // kiosk is the TV display
        thisView.webrtcSession.sendKioskDataChannelMessage({
          type: 'transcription-line',
          line: transcriptionLine,
          speaker: thisView.videoSettings?.visitorName
        })
      }

      this.transcriptionLocalMicStream = markRaw(await navigator.mediaDevices.getUserMedia({ audio: true, video: false }))
      this.transcriptionSessionToPatientTV = markRaw(new TranscriptionSession())
      this.transcriptionSessionToPatientTV.start(patientMonitorId, this.transcriptionLocalMicStream, 'caregiver', onTranscriptionLine, this.$http)
    },
    stopTranscriptionToPatientTV() {
      if (this.transcriptionSessionToPatientTV) {
        this.transcriptionSessionToPatientTV.stop();
        this.transcriptionSessionToPatientTV = null;
        if (this.transcriptionLocalMicStream) {
          this.transcriptionLocalMicStream.getTracks().forEach((track) => {
            track.stop();
          });
          this.transcriptionLocalMicStream = null;
          console.info('stopped transcription to patient TV local mic stream');
        }
      }
    },
    stopVideo() {
      console.log("stopping video...");
      this.onConnectSucceeded?.reset();
      this.disconnectAimeeDemo();
      this.transcriptionEnabled = false;
      // this.$emit('transcription-toggled', false);
      this.cachedPatientMonitorId = null;
      this.stopTranscriptionToPatientTV();
      if (!this.isTrainingVideo) {
        clearTimeout(this.connectionTimeout);
        clearTimeout(this.testingTimer)
        // clean up
        if (this.webrtcSession) {
          console.log("video room found. disconnecting...");
          // disconnect streams from video html elements so they can be closed properly
          const localElem = document.getElementById("webrtc-view__local-view");
          if (localElem) {
            localElem.srcObject = null;
          }
          const patientElem = document.getElementById('webrtc-view__patient-view');
          if (patientElem) {
            patientElem.srcObject = null;
          }

          this.otherParticipants.forEach((p) => {
            const pVid = document.getElementById(`other-video-${p.participantId}`);
            if (pVid) {
              pVid.srcObject = null;
              console.debug(`participant ${p.participantId} video element cleared`);
            }
          });

          // make sure we disconnect from the room after closing tracks and cleaning up elements
          this.webrtcSession.disconnect();
          console.log('disconnected from video')
          this.webrtcSession = null;
        }
        this.otherParticipants.splice(0, this.otherParticipants.length);
        if (this.meetingId) {
          to(this.$http.put(`/meetings/${this.meetingId}/leave`))
        }
      } else {
        this.webrtcSession.disconnect();
      }
      if (this.localMediaStream) {
        this.localMediaStream.getTracks().forEach((track) => {
          track.stop();
        });
        this.localMediaStream = null;
      }
      this.connecting = false;
      this.setPatientCameraIsPlaying(false)
      this.muted = false;
      this.$refs.qualityIndicator?.reset()
      this.$emit('close')
    },
    async sendSignal(signalPayload) {
      signalPayload.patient_monitor_id = this.meetingMetaData.patient_monitor?.id;
      signalPayload.video_device_id = this.meetingMetaData.device.id;
      signalPayload.meeting_id = this.meetingId
      signalPayload.ptz = this.meetingMetaData.is_ptz
      console.debug("sending signal", signalPayload);
      const [err] = await to(this.http.post('/videos/signal', signalPayload));
      if (err) {
        console.warn("error sending signal", err);
        this.$buefy.dialog.alert({
          title: 'Error',
          message: 'Video session could not connect or was disconnected. Please check that monitoring has not been stopped and your network connection is functioning'
        });
        this.log(LOG_TYPES.LIVE_VIDEO.VIDEO_CONNECT_FAILED)
        this.stopVideo()
      }
    },
    toggleMute() {
      // toggle the local audio
      if (this.isTrainingVideo) {
        this.$refs.video.muted = this.muted
        this.muted = !this.muted
        if (this.aimeeLocalMediaStream) {
          this.aimeeLocalMediaStream.getTracks().forEach((track) => {
            track.enabled = !this.muted
          });
        }
      } else {
        if (this.muted) {
          // this.webrtcSession.localParticipant.audioTracks.forEach((pub) => pub.track.enable());
          this.webrtcSession.unmuteLocalAudio();
          this.muted = false;
        } else {
          // this.webrtcSession.localParticipant.audioTracks.forEach((pub) => pub.track.disable());
          this.webrtcSession.muteLocalAudio();
          this.muted = true;
        }
      }
    },
    setLocalMediaStream() {
      if (this.localMediaStream) {
        const localElem = document.getElementById("webrtc-view__local-view");
        if (localElem) {
          localElem.srcObject = this.localMediaStream;
          console.debug("Connected local media stream");
        }
      }
    },
    ...mapMutations('LiveVideoStore', ['setPatientCameraIsPlaying']),
    log(type) {
      if (!this.meetingMetaData.test_mode) {
        const videoType = this.meetingMetaData.activateVideo ? 'monitoring' : 'meeting'
        let videoReasons = null
        let selectedVideoReasonIds = []

        if (this.videoSettings?.selectedVideoReason) {
          selectedVideoReasonIds = [this.videoSettings.selectedVideoReason]
        } else {
          selectedVideoReasonIds = this.videoSettings?.selectedVideoReasons?.map(x => x.id) || []
        }
        videoReasons = this.videoSettings?.availableVideoReasons?.filter(x => selectedVideoReasonIds.includes(x.id))?.map(x => x.name)?.join(', ')

        this.$logger.logLiveVideo(
          type,
          videoType,
          this.meetingMetaData?.patient?.id,
          this.meetingMetaData?.patient_monitor?.id,
          this.meetingId,
          this.meetingMetaData?.is_ptz,
          videoReasons
        )
      }
    },
    // web app aimee is intended for demo purposes only at this time
    async connectAimeeDemo() {
      console.debug('generating aimee peerconnection...')
      const videoConstraints = {
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        frameRate: { ideal: 1 }
      }
      this.aimeeLocalMediaStream = markRaw(await navigator.mediaDevices.getUserMedia({ audio: true, video: videoConstraints }))
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
      });
      this.aimeePeerConnection = markRaw(pc);
      for (const track of this.aimeeLocalMediaStream.getTracks()) {
        if (track.kind === 'audio') {
          track.enabled = !this.muted;
        }
        pc.addTrack(track, this.aimeeLocalMediaStream)
        console.debug(`added aimee local ${track.kind} track`, track);
      }
      pc.ontrack = (e) => {
        this.$refs.aimeeAudio.srcObject = e.streams[0];
        console.debug('aimee audio track added', e.track);
      }
      const dc = pc.createDataChannel('oai-events');
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const monitorId = await this.getPatientMonitorId();
      const [err, response] = await to(this.http.post('/aimee_conversations', {
        sdp: offer.sdp,
        'patient_monitor_id': monitorId,
        'meeting_id': this.meetingId,
        'source': 'web',
        'prompt_id': null, // uses hospital or device default prompt
        'aimee_tags': this.meetingMetaData.aimee_tags
      }))
      if (!err) {
        const aimeeResponse = response.data;
        this.aimeeConversationId = aimeeResponse.aimee_conversation_id
        console.debug(`setting aimee remote description. conversation id: ${this.aimeeConversationId}`)
        await pc.setRemoteDescription({
          type: 'answer',
          sdp: aimeeResponse.sdp
        });
        console.debug('aimee peer connection established')
        this.aimeeConnected = true

        const patientId = this.meetingMetaData?.patient?.id

        dc.addEventListener('message', async (e) => {
          const data = JSON.parse(e.data);
          const msg_type = data['type'];
          if (msg_type == "response.audio_transcript.done") {
            this.aimeeTranscript += `aimee: ${data['transcript']}\n`
            this.scheduleTranscriptSave()
          } else if (msg_type == "conversation.item.input_audio_transcription.completed") {
            this.aimeeTranscript += `patient/speaker: ${data['transcript']}\n`
            this.scheduleTranscriptSave()
          } else if (data.type === 'session.created' && aimeeResponse.greeting_payload) {
            console.debug('sending greeting payload', aimeeResponse.greeting_payload)
            dc.send(JSON.stringify(aimeeResponse.greeting_payload))
          }
          else if (data.type === 'response.function_call_arguments.done') {
            console.info('aimee function call response:', data.name, data.arguments);
            if (data.name == 'get_nurse') {
              const [err, response] = await to(this.$http.get(`/patients/${patientId}/monitoring_sessions`))
              if (!err) {
                const monitor = response.data.find(x => x.status === 'active')
                if (monitor) {
                  await to(this.$http.post('/virtual_requests/create_for_aimee', {
                    key: 'nurse',
                    monitor_id: monitor.id
                  }, { skipDivisions: true }))
                }
              }
            } else if (data.name == 'trigger_alarm') {
              const [err, response] = await to(this.$http.get(`/patients/${patientId}/monitoring_sessions`))
              if (!err) {
                const monitor = response.data.find(x => x.status === 'active')
                if (monitor) {
                  const [err] = await to(this.$http.post('/stat_alarms', {
                    alarm_type: 'alarm',
                    patient_monitor_id: monitor.id
                  }))
                  if (!err) {
                    this.$buefy.toast.open({
                      message: 'Alarm event has been successfully activated.',
                      type: 'is-success',
                      duration: 5000
                    });
                  }
                }
              }
            } else {
              console.warn('unknown function call', data.name);
            }
          }
        });
      }

    },
    async disconnectAimeeDemo() {
      if (this.aimeeTranscriptSaveTimer) {
        clearTimeout(this.aimeeTranscriptSaveTimer)
        this.aimeeTranscriptSaveTimer = null
      }

      if (this.aimeeConversationId && this.aimeeTranscript?.length > 0) {
        console.info("Aimee transcript:", this.aimeeTranscript);
        // Save the transcript to the server
        // TODO: audio recording?
        await to(this.http.post(`/aimee_conversations/${this.aimeeConversationId}/end_conversation`, {
          transcript: this.aimeeTranscript,
        }))
      } else if (this.aimeeConnected) {
        console.warn("Aimee conversation not found or no transcript available");
      }

      if (this.aimeePeerConnection) {
        this.aimeePeerConnection.close();
        this.aimeePeerConnection = null;
      }
      if (this.aimeeLocalMediaStream) {
        this.aimeeLocalMediaStream.getTracks().forEach((track) => {
          track.stop();
        });
      }
      this.aimeeLocalMediaStream = null;
      if (this.$refs.aimeeAudio) {
        this.$refs.aimeeAudio.srcObject = null;
      }
      this.aimeeConversationId = null;
      this.aimeeTranscript = '';
      this.lastSavedTranscript = '';
      this.aimeeConnected = false;

    },
    scheduleTranscriptSave() {
      const autoSaveSeconds = this.meetingMetaData.auto_save || 0
      if (autoSaveSeconds <= 0) {
        return
      }

      if (this.aimeeTranscriptSaveTimer) {
        clearTimeout(this.aimeeTranscriptSaveTimer)
      }

      this.aimeeTranscriptSaveTimer = setTimeout(() => {
        this.updateTranscript()
      }, autoSaveSeconds * 1000)
    },
    async updateTranscript() {
      if (!this.aimeeConversationId ||
        this.aimeeTranscript === this.lastSavedTranscript ||
        this.aimeeTranscript.length === 0) {
        return
      }

      await to(this.http.put(`/aimee_conversations/${this.aimeeConversationId}/update_transcript`, {
        transcript: this.aimeeTranscript
      }, {
        skipLoading: true,
        handleErrors: true,
      }))

      this.lastSavedTranscript = this.aimeeTranscript
    },
    toggleTranscription() {
      this.transcriptionEnabled = !this.transcriptionEnabled;
      console.log(`Transcription ${this.transcriptionEnabled ? 'enabled' : 'disabled'}`);
      // this.$emit('transcription-toggled', this.transcriptionEnabled);

      if (this.transcriptionEnabled) {
        // If video is active and kiosk mode is enabled, also transcribe to patient TV
        if (this.videoSettings?.activateVideo && this.meetingMetaData?.kiosk_mode && !this.isTrainingVideo) {
          this.transcribeToPatientTV();
        }
      } else {
        // Stop transcription to patient TV when CC is disabled
        this.stopTranscriptionToPatientTV();
      }
    },
    async getPatientMonitorId() {
      // Return cached value if available
      if (this.cachedPatientMonitorId) {
        return this.cachedPatientMonitorId;
      }

      let monitorId = this.meetingMetaData?.patient_monitor?.id
      if (!monitorId) {
        // depending on the entry point, the monitor may not be set
        monitorId = this.meetingMetaData?.patient?.patient_monitors?.find(x => x.status === 'active')?.id
        if (!monitorId) {
          const [err, response] = await to(this.$http.get(`/patients/${this.meetingMetaData?.patient?.id}/monitoring_sessions`))
          if (!err) {
            const monitor = response.data.find(x => x.status === 'active')
            if (monitor) {
              monitorId = monitor.id
            } else {
              console.warn('no active monitor found for patient', this.meetingMetaData?.patient?.id)
            }
          } else {
            console.error('error getting patient monitors', err)
          }
        }
      }

      // Cache the result before returning
      this.cachedPatientMonitorId = monitorId;
      return monitorId;
    },
    getPatientAudioStream() {
      // Get only the audio tracks from the patient's stream (video not needed for transcription)
      if (this.$refs.webrtcView && this.$refs.webrtcView.srcObject) {
        const audioTracks = this.$refs.webrtcView.srcObject.getAudioTracks();
        if (audioTracks.length > 0) {
          return new MediaStream(audioTracks);
        }
      }
      return null;
    },
  },
  computed: {
    ...mapState('LiveVideoStore', ['isPatientCameraPlaying', 'requestVideoStart']),
    connectButtonText() {
      return this.cancelable ? "Cancel" : "Video Visit"
    },
    cancelable() {
      return this.connecting && this.webrtcSession
    },
    isTrainingVideo() {
      return this.meetingMetaData?.device?.kind == 'trainingvideo'
    },
    isWifiNetworkInterface() {
      return this.meetingMetaData?.device?.network_interface?.toLowerCase() == 'wifi'
    },
    isQualityIndicatorEnabled() {
      return this.$store.state.currentUser.enabled_flags.includes('connection_quality_indicator')
    },
    isClosedCaptionsEnabled() {
      return this.$store.state.currentUser.enabled_flags.includes('closed_captions')
    },
    canEditAimeeConfiguration() {
      return this.$can('Manage', 'AimeePrompt')
    },
    isAimeeEnabled() {
      return this.$can('Use', 'Aimee')
    },
    supportsNightMode() {
      return this.meetingMetaData?.device?.version &&
        semver.gte(this.meetingMetaData.device.version, '1.17.16') &&
        (
          (this.meetingMetaData.is_ptz && this.meetingMetaData?.device?.config?.webrtc?.secondary_camera?.video_source_connection?.profile_name === 'vhd-ptz-usb') ||
          (!this.meetingMetaData.is_ptz && this.meetingMetaData?.device?.config?.webrtc?.primary_camera?.video_source_connection?.profile_name === 'vhd-videobar-usb-1')
        )
    },
    wifiMetrics() {
      return {
        state: this.meetingMetaData.device?.state?.metrics?.wifi_signal_state,
        strength: this.meetingMetaData.device?.state?.metrics?.wifi_signal_strength
      }
    }
  },
  mounted() {
    if (this.startAutomatically) {
      this.startVideo()
    }
    this.onConnectSucceeded = this.$helpers.once(() => {
      this.log(LOG_TYPES.LIVE_VIDEO.CONNECT_SUCCEEDED)
      if (this.meetingMetaData.auto_aimee && this.isAimeeEnabled) {
        this.connectAimeeDemo()
      }
    })
  },
  created() {
    // add a window listener to make sure we disconnect from the room if the user navigates away or closes the window
    window.addEventListener('beforeunload', this.stopVideo);
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.stopVideo)
  },
  watch: {
    requestVideoStart: {
      handler(newValue) {
        if (newValue && !this.startAutomatically) {
          this.startVideo()
        }
      },
      immediate: true
    }
  },
  data() {
    return {
      connectionTimeout: null,
      testingTimer: null,
      testVideoDuration: 5 * 60 * 1000,
      connecting: false,
      /**
       * @type {?WebRTCSession}
       */
      webrtcSession: null,
      otherParticipants: [],
      muted: false,
      meetingId: null,
      aimeeConnected: false,
      aimeePeerConnection: null,
      onConnectSucceeded: null,
      aimeeConversationId: null,
      aimeeTranscript: '',
      aimeeLocalMediaStream: null,
      aimeeTranscriptSaveTimer: null,
      lastSavedTranscript: '',
      transcriptionEnabled: false,
      cachedPatientMonitorId: null,
      transcriptionSessionToPatientTV: null,
      transcriptionLocalMicStream: null,
      ccOnIcon,
      ccSlashIcon
    }
  }
}
</script>

<style scoped lang="scss">
.webrtc-view__tracks {
  position: relative;
  width: 100%;
  height: 100%;
}

.webrtc-view__settings {
  position: absolute;
  top: 10px;
  left: 12px;
  z-index: 2;
}

.webrtc-view__not-connected {
  position: relative;
  top: 0;
  width: 100%;
  height: 100%;
  background-image: url("@/assets/blurry_hospital.jpg");
  background-size: cover;
  background-position: center center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  button {
    margin-top: 10px;
  }
}

#webrtc-view__other-participants {
  top: 10px;
  right: 10px;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: hidden;
  z-index: 2;
  margin: 20px 10px;
  padding: 5px;
}

:deep() {
  #webrtc-view__patient-view {
    width: 100%;
  }
}

.participant-container {
  position: relative;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  flex: 1 1 0;
  min-height: 0;
  aspect-ratio: 16 / 9;
  width: auto;
  max-width: 260px;
  align-self: flex-end;
  max-height: 148px;
  box-shadow: 0px 0px 3px 1px #0000008a;
}

.participant-container video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.participant-name {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}


.webrtc-view__video-controls {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hangup-button {
  background-color: rgb(247 79 79 / 56%) !important;
  border: solid 1px #ff7d7d !important;

  &:hover {
    background-color: #ff7d7d !important;
  }
}

.webrtc-view__video-control {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  margin: 0 10px;
  background-color: #4fc3f78f;
  color: #fff;
  border: solid 1px #7dd7ff;
  opacity: 0;
  transition: all 0.1s linear;
  box-shadow: none !important;

  i {
    padding-top: 5px;
    font-size: 23px;
  }

  img.aimee-icon {
    width: 30px;
    height: 30px;
  }
}

.webrtc-view__video-control:hover {
  background-color: #4fc3f7;
  color: #fff;
  border: solid 1px #7dd7ff;
}

.webrtc-view__video-control.transcription-active {
  background-color: #4fc3f7;
  border: solid 1px #7dd7ff;
}

.webrtc-view__connecting-text {
  font-size: 13px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.webrtc-view__video-container {
  position: relative;
  overflow: hidden;

  width: 100%;
  height: 100%;

  &:hover .webrtc-view__video-control {
    opacity: 1;
  }
}

.video-ptz {
  z-index: 999999;
  margin-left: auto;
}

.training-video {
  width: 100%;
}

.open-mic-warning {
  position: absolute;
  width: 100%;
  left: 0;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 18;
  font-size: 11px;
  letter-spacing: 2px;
  font-weight: bold;
  color: #fff;
  padding: 3px;
  background: red;
}

@include until($desktop) {
  .webrtc-view__video-controls {
    position: absolute;
    top: initial;
    bottom: 10px;
  }

  .webrtc-view__video-control {
    opacity: 1 !important;
  }

  .video-ptz {
    display: none;
  }

  .webrtc-view__local-view {
    display: none;
  }
}

.bottom-row {
  // position: absolute;
  bottom: 5px;
  width: 100%;
  z-index: 9999;
  display: flex;
  padding: 10px 10px;
}

.overlay-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
}
</style>
